import { PencilSimple } from "@phosphor-icons/react";
import { TElement } from "@udecode/plate-common";
import { addHours, isEqual } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import {
    ControlledCheckboxInput,
    ControlledSelectInput,
    ControlledTextInput,
} from "@/components/FormInputs";
import { DateTimeInput } from "@/components/inputs/DateTimeInput";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { PollInput, PollT } from "@/components/inputs/PollInput/PollInput";
import { isUpdatePollOption } from "@/components/inputs/PollInput/utils";
import { VideoInput, VideoUploadState } from "@/components/inputs/VideoInput";
import { Button, buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { AddAnalysisInput, AddPollInput, UpdatePollInput } from "@/gen/graphql";
import { GameId, GameMetadata, GamePlyData } from "@/gen/realtime";
import { useFormFields } from "@/hooks/useForm";
import { useRealtimeGame } from "@/hooks/useRealtimeGame";
import { useViewerContext } from "@/hooks/useViewerContext";
import FeedRichTextEditor from "@/routes/game-feed/components/FeedRichTextEditor";
import { FeedEntryMode } from "@/routes/game-feed/components/LiveCommentaryForm";
import { useAllFeedIconOptions } from "@/routes/game-feed/hooks/useAllFeedIconOptions";
import {
    updateFeedRecordMutation,
    useAddAnalysis,
    useAddPoll,
    useUpdatePoll,
} from "@/routes/game-feed/query";
import { FeedRecordPreset, recordTypes } from "@/routes/game-feed/types";
import { cn } from "@/utils";
import { generatePositionFen } from "@/utils/chess";
import { getTailwindColor } from "@/utils/colors";
import { useLanguageCodeWithDefault } from "@/utils/language";

import ChessboardAnalysisInput from "./components/ChessboardAnalysisInput";

const publishDraftFeedRecordMutation = graphql(`
    mutation publishDraftFeedRecord($id: UUID!) {
        publishDraftFeedRecord(id: $id) {
            id
        }
    }
`);

const deleteFeedRecordMutation = graphql(`
    mutation deleteFeedRecord($id: UUID!) {
        deleteFeedRecord(id: $id) {
            id
        }
    }
`);

const useFeedRecord = (feedRecordId?: string) => {
    const [result] = useQuery({
        query: graphql(`
            query getFeedRecord($id: UUID!) {
                feedRecord(id: $id) {
                    id
                    ...EditFeedRecordFormContent_FeedRecord
                }
            }
        `),
        variables: { id: feedRecordId! },
        pause: !feedRecordId,
    });

    return {
        feedRecord: result.data?.feedRecord,
        loading: result.fetching,
        error: result.error,
    };
};

export const EditFeedRecordRoute = () => {
    const { feedRecordId, gameId } = useParams<{
        feedRecordId: string;
        gameId: string;
    }>();
    const languageCode = useLanguageCodeWithDefault();

    const navigate = useNavigate();
    const { feedRecord } = useFeedRecord(feedRecordId);
    const { game, plyData } = useRealtimeGame((gameId ?? "") as GameId, languageCode);

    if (!feedRecord || !game || !plyData) {
        return null;
    }

    return (
        <EditFeedRecordFormContent
            feedRecordFragment={feedRecord}
            game={game}
            plyData={plyData}
            onClose={() => {
                navigate(-1);
            }}
        />
    );
};

const EditFeedRecordFormContent_FeedRecord = graphql(`
    fragment EditFeedRecordFormContent_FeedRecord on FeedRecord {
        id
        timestamp
        content
        highlight
        bgColor
        textColor
        twitterUrl
        youtubeUrl
        imageId
        iconImageId
        draft
        ply {
            id
            san
            plyIndex
        }
        parentPly {
            id
            san
            plyIndex
        }
        analysis {
            id
            startingPositionFen
            annotatedPlies {
                plyIndex
                san
                arrows {
                    from
                    to
                    color
                }
                highlightedSquares
                pieceEmojis
            }
        }
        poll {
            id
            title
            endAt
            options {
                id
                text
            }
        }
        videoId
    }
`);

function guessPresetFromBackgroundColor(backgroundColor: string | null) {
    const presetMatch = Object.entries(recordTypes).find(
        ([_key, rt]) => rt.background === backgroundColor
    );
    return (presetMatch?.[0] ?? null) as FeedRecordPreset | null;
}

export const EditFeedRecordFormContent = ({
    feedRecordFragment,
    game,
    plyData,
    onClose,
}: {
    feedRecordFragment: FragmentType<typeof EditFeedRecordFormContent_FeedRecord>;
    game: GameMetadata;
    plyData: GamePlyData;
    onClose: () => void;
}) => {
    const { viewer } = useViewerContext();
    const feedRecord = getFragmentData(EditFeedRecordFormContent_FeedRecord, feedRecordFragment);
    const [mode] = useState<FeedEntryMode>(() => {
        if (feedRecord.poll) {
            return "poll";
        }
        if (feedRecord.twitterUrl) {
            return "twitter";
        }
        if (feedRecord.youtubeUrl) {
            return "youtube";
        }
        return "misc";
    });

    const [selectedRecordType, setSelectedRecordType] = useState<FeedRecordPreset | null>(
        guessPresetFromBackgroundColor(feedRecord.bgColor)
    );

    const [enableAnalysisBoard] = useState<boolean>(Boolean(feedRecord.analysis));

    const { fen, ply } = useMemo(() => {
        const ply = feedRecord.ply || feedRecord.parentPly;

        return {
            fen: generatePositionFen({
                startingPosition: plyData.startingPositionFen,
                sanPlies: plyData.plies.map(ply => ply.san),
                plyIndex: ply && ply.plyIndex >= 0 ? ply.plyIndex - 1 : -1,
            }),
            ply,
        };
    }, [feedRecord.parentPly, feedRecord.ply, plyData.plies, plyData.startingPositionFen]);

    const form = useFormFields<{
        timestamp: Date;
        content: TElement[] | null;
        highlight: boolean;
        bgColor: string | null;
        textColor: string | null;
        twitterUrl: string | null;
        youtubeUrl: string | null;
        imageId: string | null;
        iconImageId: string | null;
        analysis: AddAnalysisInput | null;
        poll: PollT | null;
        videoUploadState: VideoUploadState | null;
    }>({
        timestamp: new Date(feedRecord.timestamp),
        content: feedRecord.content as TElement[],
        highlight: feedRecord.highlight,
        bgColor: feedRecord.bgColor,
        textColor: feedRecord.textColor,
        twitterUrl: feedRecord.twitterUrl,
        youtubeUrl: feedRecord.youtubeUrl,
        imageId: feedRecord.imageId,
        iconImageId: feedRecord.iconImageId,
        analysis: feedRecord.analysis
            ? {
                  startingPositionFen: feedRecord.analysis.startingPositionFen,
                  annotatedPlies: feedRecord.analysis.annotatedPlies.map(ply => ({
                      plyIndex: ply.plyIndex,
                      san: ply.san,
                      arrows: ply.arrows,
                      highlightedSquares: ply.highlightedSquares,
                      pieceEmojis: ply.pieceEmojis,
                  })),
              }
            : null,
        poll: feedRecord.poll,
        videoUploadState: {
            videoId: feedRecord.videoId,
            progress: 100,
        },
    });

    const [{ error: _errorUpdateRecord }, updateFeedRecord] = useMutation(updateFeedRecordMutation);

    const [{ error: _errorAddAnalysis }, addAnalysis] = useAddAnalysis();

    const [{ error: _errorAddPoll }, addPoll] = useAddPoll();
    const [{ error: _errorUpdatePoll }, updatePoll] = useUpdatePoll();

    const [{ error: _errorPublishDraftFeedRecord }, publishDraftFeedRecord] = useMutation(
        publishDraftFeedRecordMutation
    );

    const [{ error: _errorDeleteFeedRecord }, deleteFeedRecord] =
        useMutation(deleteFeedRecordMutation);

    const hasChanges = useMemo(() => {
        return (
            form.values.content !== feedRecord.content ||
            isEqual(form.values.timestamp, new Date(feedRecord.timestamp)) ||
            form.values.highlight !== feedRecord.highlight ||
            form.values.bgColor !== feedRecord.bgColor ||
            form.values.textColor !== feedRecord.textColor ||
            form.values.twitterUrl !== feedRecord.twitterUrl ||
            form.values.youtubeUrl !== feedRecord.youtubeUrl ||
            form.values.imageId !== feedRecord.imageId ||
            form.values.iconImageId !== feedRecord.iconImageId ||
            form.values.videoUploadState?.videoId !== feedRecord.videoId ||
            form.values.analysis !== null || // TODO: Diff this properly
            form.values.poll !== null // TODO: Diff this properly
        );
    }, [
        form.values.content,
        form.values.timestamp,
        form.values.highlight,
        form.values.bgColor,
        form.values.textColor,
        form.values.twitterUrl,
        form.values.youtubeUrl,
        form.values.imageId,
        form.values.iconImageId,
        form.values.videoUploadState?.videoId,
        form.values.analysis,
        form.values.poll,
        feedRecord.content,
        feedRecord.timestamp,
        feedRecord.highlight,
        feedRecord.bgColor,
        feedRecord.textColor,
        feedRecord.twitterUrl,
        feedRecord.youtubeUrl,
        feedRecord.imageId,
        feedRecord.iconImageId,
        feedRecord.videoId,
    ]);

    const stayOrLeave = useCallback(() => {
        if (!hasChanges) {
            onClose();
            return;
        }
        window.confirm("You have unsaved changes. Are you sure you want to close this modal?")
            ? onClose()
            : null;
    }, [hasChanges, onClose]);

    const onSave = useCallback(
        async (publish?: boolean) => {
            if (!feedRecord) {
                return;
            }

            console.log("form.values.videoUploadState", form.values.videoUploadState);

            if (
                form.values.videoUploadState?.videoId &&
                form.values.videoUploadState.progress !== 100
            ) {
                toast.warning("Please wait for the video to finish uploading.");
                return;
            }

            // TODO: Don't always add a new analysis, but update the existing one
            let analysisId = null;
            if (enableAnalysisBoard && form.values.analysis) {
                const addAnalysisResult = await addAnalysis({
                    input: form.values.analysis,
                });

                if (addAnalysisResult.error) {
                    toast.error("Unable to add analysis", {
                        description: addAnalysisResult.error.message,
                    });
                    return;
                }

                analysisId = addAnalysisResult.data?.addAnalysis.id ?? null;
            }

            let pollId = null;
            if (form.values.poll) {
                if (feedRecord.poll) {
                    const updatePollResult = await updatePoll({
                        input: {
                            id: feedRecord.poll.id,
                            title: form.values.poll.title,
                            endAt: form.values.poll.endAt,
                            options: form.values.poll.options.map(option => {
                                if (isUpdatePollOption(option)) {
                                    return {
                                        id: option.id,
                                        text: option.text,
                                    } satisfies UpdatePollInput["options"][number];
                                }
                                return {
                                    text: option.text,
                                };
                            }),
                        },
                    });

                    if (updatePollResult.error) {
                        toast.error("Unable to update poll", {
                            description: updatePollResult.error.message,
                        });
                        return;
                    }
                    pollId = updatePollResult.data?.updatePoll.id ?? null;
                } else {
                    const addPollResult = await addPoll({
                        input: form.values.poll,
                    });

                    if (addPollResult.error) {
                        toast.error("Unable to add poll", {
                            description: addPollResult.error.message,
                        });
                        return;
                    }

                    pollId = addPollResult.data?.addPoll.id ?? null;
                }
            }

            const updateResult = await updateFeedRecord({
                input: {
                    id: feedRecord.id,
                    timestamp: form.values.timestamp.toISOString(),
                    content: form.values.content,
                    highlight: form.values.highlight,
                    bgColor: form.values.bgColor,
                    textColor: form.values.textColor,
                    twitterUrl: form.values.twitterUrl === "" ? null : form.values.twitterUrl,
                    youtubeUrl: form.values.youtubeUrl === "" ? null : form.values.youtubeUrl,
                    imageId: form.values.imageId,
                    iconImageId: form.values.iconImageId,
                    plyId: feedRecord.ply?.id,
                    analysisId,
                    pollId,
                    commentatorId: viewer?.commentator?.id,
                    videoId: form.values.videoUploadState?.videoId,
                },
            });

            if (updateResult.error) {
                toast.error("Unable to update feed record", {
                    description: updateResult.error.message,
                });
                return;
            }

            if (publish) {
                const publishResult = await publishDraftFeedRecord({
                    id: feedRecord.id,
                });

                if (publishResult.error) {
                    toast.error("Unable to publish feed record", {
                        description: publishResult.error.message,
                    });
                    return;
                }
            }

            onClose();
        },
        [
            feedRecord,
            form.values.videoUploadState,
            form.values.analysis,
            form.values.poll,
            form.values.timestamp,
            form.values.content,
            form.values.highlight,
            form.values.bgColor,
            form.values.textColor,
            form.values.twitterUrl,
            form.values.youtubeUrl,
            form.values.imageId,
            form.values.iconImageId,
            enableAnalysisBoard,
            updateFeedRecord,
            viewer?.commentator?.id,
            onClose,
            addAnalysis,
            updatePoll,
            addPoll,
            publishDraftFeedRecord,
        ]
    );

    const updateRecordType = (recordType: FeedRecordPreset | null) => {
        setSelectedRecordType(recordType);
        if (recordType) {
            form.setValue("bgColor", recordTypes[recordType].background);
            form.setValue("iconImageId", null);
        } else {
            form.setValue("bgColor", null);
            form.setValue("iconImageId", null);
        }
    };
    const feedIconOptions = useAllFeedIconOptions();

    return (
        <Dialog open onOpenChange={stayOrLeave}>
            <DialogTrigger className={cn(buttonVariants())}>
                <PencilSimple />
            </DialogTrigger>
            <DialogContent className="max-h-screen overflow-y-auto" aria-describedby={undefined}>
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Edit record {feedRecord.draft ? "(draft)" : ""}</DialogTitle>
                    </div>
                </DialogHeader>

                {ply ? (
                    <div className="flex flex-row items-center gap-2">
                        <span
                            className={cn(
                                "h-4 w-4 rounded-3xl border-2",
                                Math.floor(ply.plyIndex / 2) % 2 === 0 ? "bg-black" : "bg-white"
                            )}
                        />
                        <span className="text-sm font-semibold">
                            {`Move: ${Math.round((ply.plyIndex + 1) / 2)}. ${ply.san}`}
                        </span>
                    </div>
                ) : null}

                {ply ? (
                    <div className="flex w-full flex-col space-y-4">
                        <div
                            className={cn(
                                "",
                                selectedRecordType != null
                                    ? getTailwindColor(recordTypes[selectedRecordType].background)
                                    : "bg-brand-black text-brand-white"
                            )}
                        >
                            <FeedRichTextEditor
                                initialValue={form.values.content}
                                preset={selectedRecordType}
                                onPresetChange={updateRecordType}
                                onChange={value => form.setValue("content", value)}
                                game={game}
                            />
                        </div>
                        <div className="flex flex-col space-y-4">
                            <ControlledCheckboxInput
                                checked={enableAnalysisBoard}
                                label="Add analysis"
                                onValue={() => {}}
                            />
                            {enableAnalysisBoard ? (
                                <ChessboardAnalysisInput
                                    analysis={
                                        form.values.analysis ?? {
                                            startingPositionFen: fen,
                                            annotatedPlies: [
                                                {
                                                    plyIndex: 0,
                                                    san: ply.san,
                                                    arrows: [],
                                                    highlightedSquares: {},
                                                    pieceEmojis: {},
                                                },
                                            ],
                                        }
                                    }
                                    setAnalysis={analysis => form.setValue("analysis", analysis)}
                                />
                            ) : null}
                        </div>
                        {!selectedRecordType ? (
                            <ControlledSelectInput
                                className={"w-1/3"}
                                label="Select icon"
                                placeholder="No icon"
                                options={feedIconOptions}
                                onValue={value => {
                                    form.setValue("iconImageId", value);
                                }}
                                value={form.values.iconImageId}
                            />
                        ) : null}
                    </div>
                ) : mode === "misc" ? (
                    <div className="flex w-full flex-col space-y-4">
                        <FeedRichTextEditor
                            initialValue={form.values.content}
                            onChange={value => form.setValue("content", value)}
                            preset={selectedRecordType}
                            onPresetChange={updateRecordType}
                            game={game}
                        />
                        <ControlledSelectInput
                            className={"w-1/3"}
                            label="Select icon"
                            placeholder="No icon"
                            options={feedIconOptions}
                            onValue={value => {
                                form.setValue("iconImageId", value);
                            }}
                            value={form.values.iconImageId}
                        />

                        <ImageUploadInput
                            className="w-1/4"
                            label="Upload image"
                            imageId={form.values.imageId}
                            onImageId={imageId => form.setValue("imageId", imageId)}
                        />
                        <VideoInput
                            className={"w-1/2"}
                            label="Upload video"
                            value={form.values.videoUploadState ?? { videoId: null, progress: 0 }}
                            setValue={value => form.setValue("videoUploadState", value)}
                        />
                    </div>
                ) : mode === "poll" ? (
                    <div>
                        <PollInput
                            poll={
                                form.values.poll
                                    ? form.values.poll
                                    : ({
                                          title: "",
                                          options: [],
                                          endAt: addHours(new Date(), 2).toISOString(),
                                      } as AddPollInput)
                            }
                            setPoll={poll => form.setValue("poll", poll)}
                        />
                    </div>
                ) : mode === "twitter" ? (
                    <div>
                        <ControlledTextInput
                            value={form.values.twitterUrl ?? ""}
                            id="twitter"
                            onValue={twitterUrl => form.setValue("twitterUrl", twitterUrl)}
                            label="Twitter URL"
                            type="url"
                            className="w-full"
                        />
                    </div>
                ) : mode === "youtube" ? (
                    <div>
                        <ControlledTextInput
                            id="youtube"
                            value={form.values.youtubeUrl ?? ""}
                            onValue={youtubeUrl => form.setValue("youtubeUrl", youtubeUrl)}
                            label="Youtube URL"
                            type="url"
                            className="w-full"
                        />
                    </div>
                ) : null}
                <ControlledCheckboxInput
                    checked={form.values.highlight}
                    label="Make highlight"
                    onValue={highlight => form.setValue("highlight", highlight)}
                />
                <DateTimeInput
                    value={form.values.timestamp}
                    onValue={date => form.setValue("timestamp", date ?? null)}
                    label="Timestamp"
                />
                <DialogFooter className="sm:justify-between">
                    <div className="flex space-x-2">
                        <Button variant="secondary" onClick={stayOrLeave}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={async () => {
                                if (
                                    !window.confirm(
                                        "Are you sure you want to delete this feed record?"
                                    )
                                ) {
                                    return;
                                }

                                await deleteFeedRecord({ id: feedRecord.id });
                                onClose();
                            }}
                        >
                            Delete
                        </Button>
                    </div>
                    <div className="flex space-x-2">
                        <Button onClick={() => onSave()} disabled={!hasChanges}>
                            Save
                        </Button>
                        {feedRecord.draft ? (
                            <Button onClick={() => onSave(true)}>Save and publish</Button>
                        ) : null}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
